import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild, inject, signal, OnInit } from '@angular/core';
import { AppTableComponent } from '../../../../../shared/components/table/app-table.component';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { UpdatePageTitle } from '../../../../../store/app/app.actions';
import { TableColumn } from '@swimlane/ngx-datatable';
import { HttpParams } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { PaginatedResults } from '@bo/ng-api';
import { Employee, TopUpFrequency, TopUpFrequencyLabel } from '../../../../../_interfaces/employee.interface';
import { EmployeeApiService } from '../../../../../_services/employee-service/api/employee-api.service';
import { AvatarComponent } from '../../../../../shared/components/avatar/avatar.component';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { invitedEmployeeTableColumns } from '../../../../../_services/employee-service/employee-utils';
import { OffcanvasService } from '../../../../../_services/offcanvas-service/offcanvas.service';
import { EmptyIndicatorPipe } from '../../../../../shared/pipes/empty-indicator.pipe';
import { EmployeeService } from '../../../../../_services/employee-service/employee.service';

@Component({
  selector: 'app-invited-employees',
  imports: [AppTableComponent, DatePipe, RouterOutlet, AvatarComponent, CurrencyPipe, EmptyIndicatorPipe],
  templateUrl: './invited-employees-list.component.html',
})
export class InvitedEmployeesListComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly store = inject(Store);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly toastService = inject(ToastService);
  readonly employeeApiService = inject(EmployeeApiService);
  readonly employeeService = inject(EmployeeService);
  readonly offcanvasService = inject(OffcanvasService);

  readonly subscriptions = new Subscription();
  protected readonly TopUpFrequency = TopUpFrequency;
  protected readonly TopUpFrequencyLabel = TopUpFrequencyLabel;
  private readonly columnsInitialized = signal(false);

  columns: Array<TableColumn> = [];
  queryParams = new HttpParams();
  disableResend: { [key: string]: boolean } = {};

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: TemplateRef<any> can be fixed by refactoring the @bo/ng-datatable library.
  @ViewChild('employeeNameCell', { static: false }) employeeNameCell!: TemplateRef<any>;
  @ViewChild('dateCreatedCell', { static: false }) dateCreatedCell!: TemplateRef<any>;
  @ViewChild('frequencyCell', { static: false }) frequencyCell!: TemplateRef<any>;
  @ViewChild('targetBalanceCell', { static: false }) targetBalanceCell!: TemplateRef<any>;
  @ViewChild('actionsCell', { static: false }) actionsCell!: TemplateRef<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Invited Employees'));
    this.offcanvasService.closeAfterNavigationEnd();
  }

  ngOnInit() {
    // Initialise columns early to prevent expression changed error
    this.columns = invitedEmployeeTableColumns;
  }

  ngAfterViewInit(): void {
    this.setupColumns();
  }

  private setupColumns(): void {
    if (this.columnsInitialized()) return;

    // Set templates on columns
    for (const column of this.columns) {
      if (column.prop === 'first_name') column.cellTemplate = this.employeeNameCell;
      if (column.prop === 'created') column.cellTemplate = this.dateCreatedCell;
      if (column.prop === 'topup_frequency') column.cellTemplate = this.frequencyCell;
      if (column.prop === 'topup_amount') column.cellTemplate = this.targetBalanceCell;
      if (column.prop === 'actions') column.cellTemplate = this.actionsCell;
    }

    // Reassign array to trigger change detection in the table
    this.columns = [...this.columns];
    this.columnsInitialized.set(true);
  }

  getData(params: HttpParams): Observable<PaginatedResults<Array<Employee>>> {
    return this.employeeApiService.getInvitedEmployees(params);
  }

  resendEmployeeInvitation(employeeId: string) {
    this.subscriptions.add(
      this.employeeApiService.resendEmployeeInvite(employeeId).subscribe({
        next: () => {},
        complete: () => {
          this.disableResend[employeeId] = true;
          this.toastService.show('Invitation email resent.', 'iconoir-check-circle');
        },
        error: () => {
          this.toastService.show('Something went wrong. This invitation could not be sent.', 'iconoir-xmark');
        },
      }),
    );
  }

  onAddEmployee() {
    this.employeeService.onAddEmployee();
  }

  onEditEmployee(id: string): void {
    this.employeeService.onEditEmployee(id);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
